import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

const nf = new Intl.NumberFormat("en-GB");
const REFRESH_INTERVAL_MS = 30 * 60 * 1000;

const domainOrder = [
  ["flights", "Flights", "✈"],
  ["terminals", "Terminals", "▦"],
  ["queues", "Queues", "◫"],
  ["road", "Roads", "▰"],
  ["rail", "Rail & TfL", "▤"],
  ["weather", "Weather", "☁"],
  ["tracking", "Tracking", "◎"],
];

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function fmtNum(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return nf.format(n);
}

function fmtPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(n % 1 ? 1 : 0)}%`;
}

function fmtDelta(value, suffix = "") {
  const n = Number(value) || 0;
  if (n > 0) return `+${fmtNum(n)}${suffix}`;
  return `${fmtNum(n)}${suffix}`;
}

function fmtAge(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n)) return "Unknown age";
  if (n < 90) return "Just updated";
  if (n < 3600) return `${Math.round(n / 60)} min old`;
  return `${Math.round(n / 3600)} hr old`;
}

function fmtTime(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "short",
    timeZone: "Europe/London",
  }).format(date);
}

function fmtClock(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 5) || "N/A";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  }).format(date);
}

function fmtDuration(value) {
  const mins = Number(value);
  if (!Number.isFinite(mins) || mins <= 0) return "N/A";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (!h) return `${m}m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function addMinutes(value, mins) {
  const base = value ? new Date(value) : new Date();
  if (Number.isNaN(base.getTime()) || !Number.isFinite(Number(mins))) return null;
  return new Date(base.getTime() + Number(mins) * 60000).toISOString();
}

function durationBetweenMinutes(startValue, endValue) {
  if (!startValue || !endValue) return 0;
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const mins = Math.round((end.getTime() - start.getTime()) / 60000);
  return mins > 0 ? mins : 0;
}

function normStatus(value) {
  return String(value || "unknown").toLowerCase();
}

function statusLabel(value) {
  const status = normStatus(value);
  if (status === "critical_red") return "Critical";
  if (status === "red") return "High";
  if (status === "amber") return "Moderate";
  if (status === "light_green") return "Watch";
  if (status === "green") return "Good";
  return "Checking";
}

function tone(value) {
  const status = normStatus(value);
  if (status === "critical_red" || status === "red") {
    return { bg: "#3b1118", soft: "rgba(239, 68, 68, 0.16)", border: "rgba(248, 113, 113, 0.42)", fg: "#fecaca", accent: "#ef4444" };
  }
  if (status === "amber") {
    return { bg: "#34220a", soft: "rgba(245, 158, 11, 0.18)", border: "rgba(245, 158, 11, 0.42)", fg: "#fde68a", accent: "#f59e0b" };
  }
  if (status === "light_green") {
    return { bg: "#102b21", soft: "rgba(34, 197, 94, 0.14)", border: "rgba(74, 222, 128, 0.30)", fg: "#bbf7d0", accent: "#22c55e" };
  }
  if (status === "green") {
    return { bg: "#0f2a1a", soft: "rgba(34, 197, 94, 0.16)", border: "rgba(74, 222, 128, 0.32)", fg: "#dcfce7", accent: "#16a34a" };
  }
  return { bg: "#172033", soft: "rgba(148, 163, 184, 0.14)", border: "rgba(148, 163, 184, 0.28)", fg: "#cbd5e1", accent: "#94a3b8" };
}

function compareTrend(delta, higherIsBad = true) {
  const n = Number(delta) || 0;
  if (n === 0) return { text: "flat", color: "#94a3b8" };
  const bad = higherIsBad ? n > 0 : n < 0;
  return { text: n > 0 ? "up" : "down", color: bad ? "#fb7185" : "#4ade80" };
}

function pickKpis(data) {
  const reports = data?.analytics?.reports || {};
  const kpis = reports?.overview?.kpis || {};
  const deltas = reports?.compare?.summary?.deltas || {};
  return [
    { label: "Flights", value: fmtNum(kpis.total_flights), delta: deltas.total_flights?.delta, higherIsBad: false },
    { label: "Delayed 15m+", value: fmtNum(kpis.delayed_15), sub: fmtPct(kpis.delayed_15_rate_pct), delta: deltas.delayed_15?.delta },
    { label: "Cancelled", value: fmtNum(kpis.cancelled), sub: fmtPct(kpis.cancellation_rate_pct), delta: deltas.cancelled?.delta },
    { label: "On time", value: fmtPct(kpis.on_time_rate_pct), delta: deltas.on_time_rate_pct?.delta, suffix: " pts", higherIsBad: false },
    { label: "Avg delay", value: `${fmtNum(kpis.avg_delay_mins)}m`, delta: deltas.avg_delay_mins?.delta, suffix: "m" },
  ];
}

function groupAlerts(items) {
  return safeList(items).reduce((acc, item) => {
    const key = item.system || "other";
    acc[key] = acc[key] || [];
    acc[key].push(item);
    return acc;
  }, {});
}

function useCompact(maxWidth = 900) {
  const getMatch = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  };
  const [compact, setCompact] = useState(getMatch);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (event) => setCompact(!!event.matches);
    setCompact(media.matches);
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, [maxWidth]);
  return compact;
}

function topDomainRows(domains = {}) {
  return domainOrder.map(([key, label, icon]) => ({
    key,
    label,
    icon,
    ...(domains?.[key] || { domain: key, status: "unknown", score: 0, headline: `${label} checking`, reasons: [] }),
  }));
}

function roadJourneyLabel(row = {}) {
  const key = String(row.corridor_key || "");
  if (key.includes("gatwick")) return { title: "Gatwick -> Heathrow", via: "via M23 & M25" };
  if (key.includes("stansted")) return { title: "Stansted -> Heathrow", via: "via M11 & M25" };
  if (key.includes("m40")) return { title: "Oxford -> Heathrow", via: "via M40 & M25" };
  if (key.includes("m4_j13")) return { title: "Reading -> Heathrow", via: "via M4 eastbound" };
  if (key.includes("m4_lhr3_j1")) return { title: "London -> Heathrow", via: "via M4 westbound" };
  return { title: row.display_name || "Road journey", via: row.direction || "road corridor" };
}

function railJourneyLabel(row = {}) {
  const key = String(row.target?.target_key || row.target_key || row.corridor_key || row.display_name || "");
  const family = String(row.selected_journey?.operator_family || row.target?.metadata?.preferred_family || row.metadata?.preferred_family || row.corridor_mode || "").toLowerCase();
  const label = String(row.display_name || row.target?.display_name || "");
  const lowerLabel = label.toLowerCase();
  if (key.includes("piccadilly") || family.includes("piccadilly") || lowerLabel.includes("underground") || lowerLabel.includes("king's cross")) return { title: "Piccadilly line -> Heathrow", via: "TfL Underground" };
  if (key.includes("pad") && family.includes("heathrow")) return { title: "Heathrow Express -> Heathrow", via: "Paddington to airport" };
  if (key.includes("elizabeth") || family.includes("elizabeth") || lowerLabel.includes("elizabeth") || lowerLabel.includes("paddington")) return { title: "Elizabeth line -> Heathrow", via: row.query?.from_label || row.origin_label || label || "TfL rail" };
  return { title: label || "Rail journey", via: row.corridor_mode || row.target?.line_id || "rail corridor" };
}

function buildRoadJourneys(rows = []) {
  const order = ["gatwick", "stansted", "m4_j13", "m4_lhr3_j1", "m40"];
  return safeList(rows)
    .filter((row) => {
      const key = String(row.corridor_key || "").toLowerCase();
      const name = String(row.display_name || "").toLowerCase();
      return (key.includes("lhr") || name.includes("heathrow")) && !key.startsWith("lhr3_");
    })
    .sort((a, b) => {
      const ak = order.findIndex((part) => String(a.corridor_key || "").includes(part));
      const bk = order.findIndex((part) => String(b.corridor_key || "").includes(part));
      return (ak < 0 ? 99 : ak) - (bk < 0 ? 99 : bk);
    })
    .slice(0, 6)
    .map((row) => {
      const label = roadJourneyLabel(row);
      const duration = Number(row.live_duration_mins || 0);
      return {
        key: `road-${row.corridor_key}`,
        mode: "Road",
        icon: "▰",
        status: row.status_label,
        title: label.title,
        via: label.via,
        duration,
        delay: row.delay_mins,
        observedAt: row.observed_at_utc,
        arrivalAt: addMinutes(row.observed_at_utc, duration),
      };
    });
}

function buildRailJourneys({ railCorridors = [], tflPaths = [] } = {}) {
  const tfl = safeList(tflPaths).map((row) => {
    const label = railJourneyLabel(row);
    const startAt = row.selected_journey?.start_time || row.query?.query_time_utc || row.query?.observed_at_utc || null;
    const arrivalAt = row.selected_journey?.arrival_time || null;
    const duration = Number(row.selected_journey?.total_duration_mins || row.avg_duration_mins || row.slowest_duration_mins || durationBetweenMinutes(startAt, arrivalAt) || 0);
    return {
      key: `tfl-${row.target?.target_key || row.target_key || row.display_name}`,
      mode: "TfL",
      icon: "▤",
      status: row.status_label || row.target?.effective_status_label,
      title: label.title,
      via: label.via,
      duration,
      delay: null,
      observedAt: row.query?.observed_at_utc,
      arrivalAt: arrivalAt || addMinutes(row.query?.query_time_utc || row.query?.observed_at_utc || new Date().toISOString(), duration),
    };
  });
  const rail = safeList(railCorridors)
    .filter((row) => String(row.display_name || "").toLowerCase().includes("heathrow") && Number(row.current_duration_mins || 0) > 0)
    .map((row) => {
      const label = railJourneyLabel(row);
      const duration = Number(row.current_duration_mins || 0);
      return {
        key: `rail-${row.corridor_key}`,
        mode: "Rail",
        icon: "▤",
        status: row.status_label,
        title: label.title,
        via: label.via,
        duration,
        delay: row.delta_mins,
        observedAt: row.observed_at_utc,
        arrivalAt: addMinutes(row.observed_at_utc, duration),
      };
    });
  const preferred = ["Heathrow Express", "Elizabeth line", "Piccadilly"];
  return [...tfl, ...rail]
    .sort((a, b) => {
      const ai = preferred.findIndex((name) => a.title.includes(name));
      const bi = preferred.findIndex((name) => b.title.includes(name));
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    })
    .slice(0, 6);
}

function StatusBadge({ status }) {
  const t = tone(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 999, background: t.soft, color: t.fg, border: `1px solid ${t.border}`, fontSize: 12, fontWeight: 900 }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.accent, boxShadow: `0 0 16px ${t.accent}` }} />
      {statusLabel(status)}
    </span>
  );
}

function Panel({ title, meta, children, style }) {
  return (
    <section style={{ background: "rgba(8, 20, 38, 0.82)", border: "1px solid rgba(148, 163, 184, 0.18)", borderRadius: 8, boxShadow: "0 22px 60px rgba(0,0,0,0.24)", overflow: "hidden", ...style }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", padding: "16px 18px 10px", borderBottom: "1px solid rgba(148, 163, 184, 0.12)" }}>
        <h2 style={{ margin: 0, color: "#f8fafc", fontSize: 18, lineHeight: 1.1 }}>{title}</h2>
        {meta ? <span style={{ color: "#94a3b8", fontSize: 12, fontWeight: 700 }}>{meta}</span> : null}
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </section>
  );
}

function MetricCard({ item }) {
  const trend = compareTrend(item.delta, item.higherIsBad !== false);
  const hasDelta = Number.isFinite(Number(item.delta));
  return (
    <div style={{ padding: 16, background: "rgba(15, 35, 60, 0.78)", border: "1px solid rgba(148, 163, 184, 0.16)", borderRadius: 8, minWidth: 0 }}>
      <div style={{ color: "#9fb3ce", fontSize: 12, fontWeight: 800 }}>{item.label}</div>
      <div style={{ marginTop: 9, color: "#f8fafc", fontSize: 28, fontWeight: 950, lineHeight: 1 }}>{item.value}</div>
      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", gap: 8, color: "#94a3b8", fontSize: 12, fontWeight: 750 }}>
        <span>{item.sub || "today"}</span>
        {hasDelta ? <span style={{ color: trend.color }}>{fmtDelta(item.delta, item.suffix || "")}</span> : <span>cached</span>}
      </div>
    </div>
  );
}

function DomainCard({ domain }) {
  const t = tone(domain.status);
  const contributors = safeList(domain.top_contributors).slice(0, 2);
  return (
    <article style={{ background: `linear-gradient(180deg, ${t.soft}, rgba(15, 23, 42, 0.74))`, border: `1px solid ${t.border}`, borderRadius: 8, padding: 14, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.08)", color: t.fg, fontWeight: 900 }}>{domain.icon}</span>
          <strong style={{ color: "#f8fafc", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{domain.label}</strong>
        </div>
        <StatusBadge status={domain.status} />
      </div>
      <div style={{ marginTop: 12, color: "#e2e8f0", fontSize: 13, lineHeight: 1.35, minHeight: 35 }}>{domain.headline || "Normal"}</div>
      <div style={{ marginTop: 11, height: 6, borderRadius: 999, background: "rgba(148,163,184,0.18)", overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, Math.max(0, Number(domain.score || 0)))}%`, height: "100%", background: t.accent }} />
      </div>
      {contributors.length ? (
        <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
          {contributors.map((item) => (
            <div key={`${domain.key}-${item.target_key}-${item.summary}`} style={{ color: "#94a3b8", fontSize: 11, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {item.summary || item.title}
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

function SimpleTable({ rows, columns, empty }) {
  if (!rows.length) return <div style={{ color: "#94a3b8", fontSize: 13 }}>{empty || "No cached rows available."}</div>;
  return (
    <div style={{ display: "grid", gap: 9 }}>
      <div style={{ display: "grid", gridTemplateColumns: columns.map((col) => col[2] || "minmax(0, 1fr)").join(" "), gap: 10, alignItems: "center", padding: "0 0 7px", borderBottom: "1px solid rgba(148, 163, 184, 0.16)", color: "#9fb3ce", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>
        {columns.map(([label], colIdx) => (
          <div key={label} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: colIdx === 0 ? "left" : "left" }}>
            {label}
          </div>
        ))}
      </div>
      {rows.map((row, idx) => (
        <div key={`${idx}-${JSON.stringify(row).slice(0, 50)}`} style={{ display: "grid", gridTemplateColumns: columns.map((col) => col[2] || "minmax(0, 1fr)").join(" "), gap: 10, alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(148, 163, 184, 0.12)", color: "#e5eefb", fontSize: 13 }}>
          {columns.map(([label, render], colIdx) => (
            <div key={label} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: colIdx === 0 ? "normal" : "nowrap" }}>
              {render(row)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function AlertRow({ item }) {
  const t = tone(item.severity);
  const primary = item.summary || item.title || item.category || item.system;
  const secondary = item.summary && item.title && item.summary !== item.title ? item.title : item.category || item.system;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "42px minmax(0, 1fr) auto", gap: 12, alignItems: "center", padding: 12, background: "rgba(15, 35, 60, 0.62)", border: `1px solid ${t.border}`, borderRadius: 8 }}>
      <div style={{ width: 38, height: 38, borderRadius: 8, display: "grid", placeItems: "center", background: t.soft, color: t.fg, fontWeight: 950 }}>{String(item.system || "?").slice(0, 2).toUpperCase()}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "#f8fafc", fontSize: 14, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{primary}</div>
        <div style={{ marginTop: 4, color: "#9fb3ce", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{secondary}</div>
      </div>
      <StatusBadge status={item.severity} />
    </div>
  );
}

function JourneyRow({ item }) {
  const t = tone(item.status);
  const punctuality = normStatus(item.status) === "green" || normStatus(item.status) === "light_green" ? "on time" : "watch timing";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "42px minmax(0, 1.45fr) auto minmax(142px, 0.7fr)", gap: 14, alignItems: "center", padding: "13px 0", borderBottom: "1px solid rgba(148, 163, 184, 0.12)" }}>
      <div style={{ width: 34, height: 34, borderRadius: 8, display: "grid", placeItems: "center", color: t.fg, background: t.soft, border: `1px solid ${t.border}`, fontWeight: 950 }}>{item.icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "#f8fafc", fontWeight: 950, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div>
        <div style={{ marginTop: 4, color: "#9fb3ce", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.via}</div>
      </div>
      <StatusBadge status={item.status} />
      <div style={{ textAlign: "right", minWidth: 0 }}>
        <div style={{ color: "#f8fafc", fontWeight: 950, fontSize: 14 }}>{fmtDuration(item.duration)}</div>
        <div style={{ marginTop: 4, color: "#9fb3ce", fontSize: 12 }}>
          Reach LHR {fmtClock(item.arrivalAt)} · {punctuality}
        </div>
      </div>
    </div>
  );
}

function JourneyImpact({ roadJourneys, railJourneys }) {
  const hasRows = roadJourneys.length || railJourneys.length;
  if (!hasRows) return null;
  return (
    <Panel title="Journey Impact" meta="to LHR now">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 22 }}>
        <div>
          <div style={{ color: "#9fb3ce", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0, marginBottom: 4 }}>Road Corridors</div>
          {(roadJourneys.length ? roadJourneys : []).map((item) => <JourneyRow key={item.key} item={item} />)}
        </div>
        <div>
          <div style={{ color: "#9fb3ce", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0, marginBottom: 4 }}>Rail & TfL</div>
          {(railJourneys.length ? railJourneys : []).map((item) => <JourneyRow key={item.key} item={item} />)}
        </div>
      </div>
    </Panel>
  );
}

function fmtHour(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  }).format(date);
}

function disruptionCount(row = {}) {
  return Number(row.delayed_15 || 0) + Number(row.cancelled || 0);
}

function disruptionRate(row = {}) {
  const flights = Number(row.total_flights || 0);
  if (!flights) return 0;
  return (disruptionCount(row) / flights) * 100;
}

function operationalConclusion(summary = {}) {
  const deltas = summary?.deltas || {};
  const current = summary?.current || {};
  const parts = [];
  if (Number.isFinite(Number(current.total_flights))) parts.push(`${fmtNum(current.total_flights)} flights today`);
  if (Number.isFinite(Number(current.delayed_15))) parts.push(`${fmtNum(current.delayed_15)} delayed 15m+`);
  if (Number.isFinite(Number(current.cancelled))) parts.push(`${fmtNum(current.cancelled)} cancelled`);

  const delayDelta = Number(deltas.delayed_15?.delta);
  const cancelDelta = Number(deltas.cancelled?.delta);
  const onTimeDelta = Number(deltas.on_time_rate_pct?.delta);
  const movement = [];
  if (Number.isFinite(delayDelta)) movement.push(`delays ${delayDelta >= 0 ? "+" : ""}${fmtNum(delayDelta)} vs yesterday`);
  if (Number.isFinite(cancelDelta)) movement.push(`cancellations ${cancelDelta >= 0 ? "+" : ""}${fmtNum(cancelDelta)}`);
  if (Number.isFinite(onTimeDelta)) movement.push(`on-time ${onTimeDelta >= 0 ? "+" : ""}${onTimeDelta.toFixed(1)} pts`);

  if (!parts.length && !movement.length) return "Cached comparison is not detailed enough for a conclusion yet.";
  return `${parts.join(" · ")}${movement.length ? `. Movement: ${movement.join(", ")}.` : "."}`;
}

function OperationalOverview({ compare }) {
  const nowMs = Date.now();
  const rows = safeList(compare?.series)
    .filter((row) => row.comparison_bucket === "current")
    .filter((row) => {
      const bucketMs = Date.parse(row.bucket_start || "");
      return Number.isFinite(bucketMs) && bucketMs <= nowMs + 5 * 60000;
    })
    .sort((a, b) => Date.parse(b.bucket_start || 0) - Date.parse(a.bucket_start || 0));
  const displayRows = rows.slice(0, 8);
  const maxAffected = Math.max(1, ...displayRows.map(disruptionCount));
  const maxFlights = Math.max(1, ...displayRows.map((row) => Number(row.total_flights || 0)));
  if (!displayRows.length) return <div style={{ color: "#94a3b8", fontSize: 13 }}>No cached flight comparison available yet.</div>;
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 0.45fr)", gap: 14, alignItems: "stretch" }}>
        <div style={{ padding: 12, borderRadius: 8, border: "1px solid rgba(148, 163, 184, 0.14)", background: "rgba(15, 35, 60, 0.44)" }}>
          <div style={{ color: "#f8fafc", fontSize: 13, fontWeight: 900 }}>What this shows</div>
          <div style={{ marginTop: 6, color: "#b8c7da", fontSize: 12, lineHeight: 1.45 }}>
            Hourly flight disruption for today. X-axis is local hour, latest first. Y-axis is affected flights
            (<strong style={{ color: "#e5eefb" }}> delayed 15m+ + cancelled</strong>). Bar height is scaled to the busiest disruption hour in this view.
          </div>
          <div style={{ marginTop: 10, color: "#dbeafe", fontSize: 12, fontWeight: 800 }}>
            Conclusion: {operationalConclusion(compare?.summary)}
          </div>
        </div>
        <div style={{ padding: 12, borderRadius: 8, border: "1px solid rgba(148, 163, 184, 0.14)", background: "rgba(15, 35, 60, 0.44)" }}>
          <div style={{ color: "#9fb3ce", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>Scale</div>
          <div style={{ marginTop: 8, color: "#f8fafc", fontSize: 18, fontWeight: 950 }}>{fmtNum(maxAffected)}</div>
          <div style={{ color: "#9fb3ce", fontSize: 12 }}>affected flights = tallest bar</div>
          <div style={{ marginTop: 8, color: "#f8fafc", fontSize: 18, fontWeight: 950 }}>{fmtNum(maxFlights)}</div>
          <div style={{ color: "#9fb3ce", fontSize: 12 }}>flights = busiest hour</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${displayRows.length}, minmax(12px, 1fr))`, gap: 6, alignItems: "end", height: 110, padding: "8px 0 0", borderBottom: "1px solid rgba(148, 163, 184, 0.18)", borderLeft: "1px solid rgba(148, 163, 184, 0.18)" }}>
        {displayRows.map((row, idx) => {
          const affected = disruptionCount(row);
          return (
            <div key={`${row.bucket_start}-${idx}`} title={`${fmtHour(row.bucket_start)} • ${fmtNum(affected)} affected • ${fmtNum(row.total_flights)} flights`} style={{ display: "grid", alignItems: "end", height: "100%" }}>
              <div style={{ minHeight: 3, height: `${Math.max(3, (affected / maxAffected) * 100)}%`, borderRadius: "5px 5px 0 0", background: affected ? "linear-gradient(180deg, #f59e0b, #ef4444)" : "rgba(74, 222, 128, 0.7)" }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", color: "#94a3b8", fontSize: 11, fontWeight: 800 }}>
        <span>X-axis: local hour, latest -> previous</span>
        <span>Y-axis: affected flights, max {fmtNum(maxAffected)}</span>
      </div>

      <SimpleTable
        rows={displayRows}
        columns={[
          ["Hour", (row) => fmtHour(row.bucket_start), "64px"],
          ["Flights", (row) => fmtNum(row.total_flights), "72px"],
          ["Affected", (row) => fmtNum(disruptionCount(row)), "78px"],
          ["Delayed 15m+", (row) => fmtNum(row.delayed_15), "92px"],
          ["Cancelled", (row) => fmtNum(row.cancelled), "76px"],
          ["Rate", (row) => fmtPct(disruptionRate(row)), "64px"],
          ["Avg delay", (row) => `${fmtNum(row.avg_delay_mins)}m`, "74px"],
        ]}
        empty="No hourly comparison rows available."
      />
    </div>
  );
}

function SideRail({ onNavigate }) {
  const items = [
    ["Summary", "◆"],
    ["Flights", "✈"],
    ["Terminals", "▦"],
    ["Queues", "◫"],
    ["Roads", "▰"],
    ["Rail & TfL", "▤"],
    ["Tracking", "◎"],
    ["Weather", "☁"],
    ["Alerts", "△"],
    ["Analytics", "▧"],
  ];
  return (
    <aside style={{ position: "sticky", top: 0, height: "100vh", padding: 18, borderRight: "1px solid rgba(148,163,184,0.16)", background: "linear-gradient(180deg, rgba(7,18,34,0.96), rgba(6,17,31,0.92))" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#f8fafc", fontWeight: 950, lineHeight: 1.05 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, display: "grid", placeItems: "center", border: "1px solid rgba(96,165,250,0.45)", color: "#93c5fd" }}>✈</div>
        <div>Airport<br />Journey Planner</div>
      </div>
      <nav style={{ marginTop: 24, display: "grid", gap: 6 }}>
        {items.map(([label, icon], index) => (
          <button
            key={label}
            type="button"
            onClick={() => label === "Analytics" ? onNavigate("/analytics-v2") : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minHeight: 38,
              padding: "0 10px",
              borderRadius: 8,
              border: "1px solid transparent",
              background: index === 0 ? "linear-gradient(135deg, #1d4ed8, #1e40af)" : "transparent",
              color: index === 0 ? "#ffffff" : "#cbd5e1",
              fontSize: 13,
              fontWeight: 850,
              textAlign: "left",
              cursor: label === "Analytics" ? "pointer" : "default",
            }}
          >
            <span style={{ width: 18, color: index === 0 ? "#dbeafe" : "#93a4bd" }}>{icon}</span>
            {label}
          </button>
        ))}
      </nav>
      <div style={{ position: "absolute", left: 18, right: 18, bottom: 18, padding: 12, borderRadius: 8, border: "1px solid rgba(148,163,184,0.16)", color: "#9fb3ce", fontSize: 11, lineHeight: 1.45 }}>
        <strong style={{ color: "#f8fafc", display: "block", marginBottom: 8 }}>Monitored Airports</strong>
        Heathrow (LHR) <span style={{ color: "#22c55e" }}>● live</span><br />
        East Midlands (EMA) <span style={{ color: "#22c55e" }}>● live</span>
      </div>
    </aside>
  );
}

export default function HeathrowStatus() {
  const navigate = useNavigate();
  const isCompact = useCompact();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Heathrow Live Status | Airport Journey Planner";
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/api/open/heathrow/status?period=today&basis=today");
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err?.message || "Unable to load Heathrow status");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    const timer = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const status = data?.status || {};
  const overall = status?.overall || {};
  const domains = useMemo(() => topDomainRows(status?.domains || {}), [status]);
  const kpis = useMemo(() => pickKpis(data), [data]);
  const alerts = safeList(data?.alerts?.items);
  const groupedAlerts = useMemo(() => groupAlerts(alerts), [alerts]);
  const reports = data?.analytics?.reports || {};
  const topReasons = safeList(overall.reasons).slice(0, 4);
  const topContributors = safeList(overall.top_contributors).slice(0, 6);
  const overallTone = tone(overall.status);
  const queueRows = safeList(reports?.terminal_queues?.overview?.latest).slice(0, 8);
  const roadRows = safeList(reports?.road?.overview?.rankings?.corridors).slice(0, 6);
  const railRows = safeList(reports?.rail?.overview?.corridors).slice(0, 6);
  const roadJourneys = useMemo(() => buildRoadJourneys(reports?.road?.overview?.rankings?.corridors), [reports]);
  const railJourneys = useMemo(
    () => buildRailJourneys({
      railCorridors: reports?.rail?.overview?.corridors,
      tflPaths: data?.journey_impact?.tfl_journey_paths || reports?.rail?.facts?.facts?.tfl_journeys,
    }),
    [reports, data]
  );
  const weatherNow = reports?.weather?.overview?.latest;
  const trackingSummary = reports?.tracking?.overview?.summary;
  const sourceErrors = data?.source_errors || {};

  return (
    <main style={{ minHeight: "100vh", background: "radial-gradient(circle at 18% 0%, rgba(37,99,235,0.20), transparent 28%), linear-gradient(180deg, #06111f 0%, #081827 42%, #07111f 100%)", color: "#f8fafc" }}>
      <Helmet>
        <title>Heathrow Live Status | Airport Journey Planner</title>
        <meta name="description" content="Live Heathrow status from cached airport operations, analytics and unified alerts. Check flights, terminals, queues, roads, rail, weather and tracking signals." />
        <link rel="canonical" href="https://www.airportjourneyplanner.com/heathrow/status" />
      </Helmet>

      <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
        <div style={{ minWidth: 0 }}>
          <section style={{ padding: isCompact ? "18px 14px 12px" : "24px 24px 14px", borderBottom: "1px solid rgba(148, 163, 184, 0.12)" }}>
            <div style={{ maxWidth: 1640, margin: "0 auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h1 style={{ margin: 0, fontSize: isCompact ? 30 : 32, lineHeight: 1.05, letterSpacing: 0 }}>Airport Operations Summary</h1>
                  <div style={{ marginTop: 6, color: "#9fb3ce", fontSize: 13, fontWeight: 750 }}>
                    Heathrow (LHR) · Facts period: today · Latest cached status
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ color: "#9fb3ce", fontSize: 12, fontWeight: 800 }}><span style={{ color: "#22c55e" }}>●</span> Cached refresh every 30 min</span>
                  <button type="button" onClick={() => navigate("/heathrow")} style={topButtonStyle}>Plan journey</button>
                  <button type="button" onClick={() => navigate("/analytics-v2")} style={topButtonStyle}>Analytics</button>
                </div>
              </div>

              {loading ? <div style={{ marginTop: 18, color: "#bfdbfe", fontWeight: 800 }}>Loading latest cached Heathrow status...</div> : null}
              {error ? <div style={{ marginTop: 18, color: "#fecaca", fontWeight: 800 }}>{error}</div> : null}

              {!loading && data ? (
                <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 0, border: "1px solid rgba(148,163,184,0.16)", borderRadius: 8, overflow: "hidden", background: "rgba(8,20,38,0.74)" }}>
                  {kpis.map((item) => <MetricCard key={item.label} item={item} />)}
                  <div style={{ padding: 16, background: overallTone.soft, borderLeft: "1px solid rgba(148,163,184,0.14)", minWidth: 0 }}>
                    <div style={{ color: "#9fb3ce", fontSize: 12, fontWeight: 800 }}>Overall Status</div>
                    <div style={{ marginTop: 9, color: overallTone.fg, fontSize: 28, fontWeight: 950, lineHeight: 1 }}>{statusLabel(overall.status)}</div>
                    <div style={{ marginTop: 8, color: "#94a3b8", fontSize: 12, fontWeight: 750 }}>Score {fmtNum(overall.score)} / 100</div>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          {!loading && data ? (
            <section style={{ padding: isCompact ? "14px" : "18px 24px 34px" }}>
              <div style={{ maxWidth: 1640, margin: "0 auto", display: "grid", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.15fr) minmax(380px, 0.85fr)", gap: 16, alignItems: "stretch" }}>
                  <Panel title="Flight Disruption Trend" meta={reports?.compare?.labels ? `${reports.compare.labels.current} vs ${reports.compare.labels.previous}` : "cached"}>
                    <OperationalOverview compare={reports?.compare} />
                    <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
                      {(topReasons.length ? topReasons : ["No major cached reasons currently reported."]).slice(0, 4).map((reason) => (
                        <div key={reason} style={{ display: "flex", gap: 10, color: "#cbd5e1", fontSize: 12, lineHeight: 1.35 }}>
                          <span style={{ color: overallTone.accent }}>●</span>
                          <span>{reason}</span>
                        </div>
                      ))}
                    </div>
                  </Panel>

                  <Panel title="Top Disruptions" meta={`${fmtNum(alerts.length)} open`}>
                    <div style={{ display: "grid", gap: 10 }}>
                      {(alerts.length ? alerts.slice(0, 6) : topContributors).map((item) => (
                        <AlertRow key={`${item.system}-${item.target_key}-${item.summary}`} item={item} />
                      ))}
                    </div>
                  </Panel>
                </div>

                <Panel title="Live Status" meta={`Status snapshot ${fmtTime(data?.freshness?.status_generated_at)}`}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
                    {domains.slice(0, 6).map((domain) => <DomainCard key={domain.key} domain={domain} />)}
                  </div>
                </Panel>

                <JourneyImpact roadJourneys={roadJourneys} railJourneys={railJourneys} />

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, alignItems: "start" }}>
              <Panel title="Terminal Queues Now" meta="Heathrow queue snapshots">
                <SimpleTable
                  rows={queueRows}
                  empty="No cached terminal queue snapshot yet."
                  columns={[
                    ["Queue", (r) => `T${r.terminal} ${r.queue_label || r.label || ""}`, "minmax(0, 1.5fr)"],
                    ["Wait", (r) => (r.max_wait_minutes == null ? "N/A" : `${fmtNum(r.max_wait_minutes)}m`), "80px"],
                    ["Trend", (r) => (r.delta_wait_minutes == null ? "flat" : fmtDelta(r.delta_wait_minutes, "m")), "80px"],
                    ["Status", (r) => statusLabel(r.status_label), "90px"],
                  ]}
                />
              </Panel>

              <Panel title="Road Corridors" meta={reports?.road?.overview ? "cached overview" : "no cache"}>
                <SimpleTable
                  rows={roadRows}
                  empty="No cached road corridor overview yet."
                  columns={[
                    ["Corridor", (r) => r.display_name || r.corridor_key, "minmax(0, 1.5fr)"],
                    ["Delay", (r) => `${fmtNum(r.delay_mins)}m`, "80px"],
                    ["Live", (r) => `${fmtNum(r.live_duration_mins)}m`, "80px"],
                    ["Status", (r) => statusLabel(r.status_label), "90px"],
                  ]}
                />
              </Panel>

              <Panel title="Rail And TfL" meta={reports?.rail?.overview ? "cached overview" : "no cache"}>
                <SimpleTable
                  rows={railRows}
                  empty="No cached rail overview yet."
                  columns={[
                    ["Corridor", (r) => r.display_name || r.corridor_key, "minmax(0, 1.5fr)"],
                    ["Delay", (r) => fmtNum(r.delayed_services_count), "70px"],
                    ["Cancel", (r) => fmtNum(r.cancelled_services_count), "70px"],
                    ["Status", (r) => statusLabel(r.status_label), "90px"],
                  ]}
                />
              </Panel>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <Panel title="Weather At LHR" meta={weatherNow ? fmtTime(weatherNow.observed_at || weatherNow.generated_at) : "cached"}>
                {weatherNow ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <BigFact label="Condition" value={weatherNow.weather_text || "Unknown"} />
                    <BigFact label="Temperature" value={`${fmtNum(weatherNow.temp_c)}C`} />
                    <BigFact label="Wind" value={`${fmtNum(weatherNow.wind_mps)}m/s`} />
                    <BigFact label="Rain" value={`${fmtNum(weatherNow.precip_mm)}mm`} />
                  </div>
                ) : <div style={{ color: "#94a3b8" }}>No cached weather overview yet.</div>}
              </Panel>

              <Panel title="Flight Tracking" meta={trackingSummary?.snapshot_date || "cached"}>
                {trackingSummary ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <BigFact label="Fresh tracked" value={fmtNum(trackingSummary.fresh_flights)} />
                    <BigFact label="Delayed signal" value={fmtNum(trackingSummary.delayed_signal_flights)} />
                    <BigFact label="Consistency" value={fmtPct(trackingSummary.consistent_rate_pct)} />
                    <BigFact label="Coverage" value={fmtNum(trackingSummary.avg_coverage_score)} />
                  </div>
                ) : <div style={{ color: "#94a3b8" }}>No cached tracking summary yet.</div>}
              </Panel>

            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, alignItems: "start" }}>
              <Panel title="Freshness" meta="read only">
                <div style={{ display: "grid", gap: 12 }}>
                  <FreshnessRow label="Status snapshot" value={fmtTime(data?.freshness?.status_generated_at)} />
                  <FreshnessRow label="Source observed" value={fmtTime(data?.freshness?.status_source_max_observed_at)} />
                  <FreshnessRow label="Analytics cache" value={fmtAge(data?.freshness?.analytics_max_age_seconds)} />
                  <FreshnessRow label="Open alerts" value={fmtNum(data?.freshness?.alerts_open_count)} />
                </div>
                {Object.keys(sourceErrors).length ? (
                  <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: "rgba(245, 158, 11, 0.12)", border: "1px solid rgba(245, 158, 11, 0.28)", color: "#fde68a", fontSize: 12, lineHeight: 1.4 }}>
                    Some cached sources are missing or stale. The page is showing the best available latest data.
                  </div>
                ) : null}
              </Panel>
              <Panel title="Alert Systems" meta="current state">
                <div style={{ display: "grid", gap: 8 }}>
                  {Object.entries(groupedAlerts).length ? Object.entries(groupedAlerts).map(([system, rows]) => (
                    <div key={system} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "9px 0", borderBottom: "1px solid rgba(148, 163, 184, 0.12)", color: "#dbeafe", fontSize: 13, fontWeight: 800 }}>
                      <span>{system.replace(/_/g, " ")}</span>
                      <span>{fmtNum(rows.length)}</span>
                    </div>
                  )) : <div style={{ color: "#94a3b8" }}>No open cached alerts.</div>}
                </div>
              </Panel>
            </div>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </main>
  );
}

function FreshnessRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, paddingBottom: 10, borderBottom: "1px solid rgba(148, 163, 184, 0.12)", fontSize: 13 }}>
      <span style={{ color: "#94a3b8", fontWeight: 800 }}>{label}</span>
      <strong style={{ color: "#f8fafc", textAlign: "right" }}>{value}</strong>
    </div>
  );
}

function BigFact({ label, value }) {
  return (
    <div style={{ minHeight: 88, padding: 14, borderRadius: 8, background: "rgba(15, 35, 60, 0.62)", border: "1px solid rgba(148, 163, 184, 0.14)" }}>
      <div style={{ color: "#94a3b8", fontSize: 12, fontWeight: 800 }}>{label}</div>
      <div style={{ marginTop: 8, color: "#f8fafc", fontSize: 22, fontWeight: 950, lineHeight: 1.1 }}>{value}</div>
    </div>
  );
}

const topButtonStyle = {
  minHeight: 40,
  padding: "0 14px",
  borderRadius: 8,
  border: "1px solid rgba(147, 197, 253, 0.28)",
  background: "rgba(15, 35, 60, 0.72)",
  color: "#dbeafe",
  fontWeight: 900,
  cursor: "pointer",
};
